import React, { useMemo, useState } from 'react';
import './activity.css';
import { useInvestmentData } from '../lib/useInvestmentData.js';
import { EMPTY_FILTERS, isActive, activeCount, applyFilters, accountOptions, whoOptions } from '../lib/activityFilters.js';
import { loggedByLabel, toCsv, csvFilename, downloadCsv } from '../lib/activityExport.js';
import { presetRange, activePreset, lastSettlementDay } from '../lib/activityGroups.js';
import { settlementSub, settlementAmount } from '../lib/settlementRows.js';
import { AdminLedgerBody } from './AdminLedgerBody.jsx';
import { PoolEntryActions } from './PoolEntryActions.jsx';

// Full value-event ledger — the bottom-nav "Activity" tab. Reuses the same
// .activity-row markup + styling as the Home feed (so it inherits the mobile
// row→card treatment), but shows the whole history grouped by month instead
// of just the latest 10.
//
// Filters sit above the ledger: origin / destination account, amount band,
// date band, whether the leg generated value, who holds that value (D21
// custody), and who logged the movement. Every filter is ANDed; the month
// groups and the totals strip describe the filtered set.
//
// The ledger itself (ActivityLedger) is shared with the admin's cross-
// household Activity screen (AdminActivity.jsx): same rows, same filters,
// same markup — the admin view adds a "Who" filter and names the household
// on every row, and its wording says "investor" where this one says "you".

const fmt$ = (n, d = 0) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const v = Math.abs(n);
  return (n < 0 ? '-' : '') + '$' + v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fmtPct = (n) => parseFloat((Number(n || 0) * 100).toFixed(3)).toString() + '%';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function monthLabel(iso) {
  const m = /^(\d{4})-(\d{2})/.exec(iso || '');
  if (!m) return 'Earlier';
  return MONTHS[Number(m[2]) - 1] + ' ' + m[1];
}

// Group events (already newest-first) into [{ label, events }] preserving order.
function groupByMonth(events) {
  const out = [];
  let cur = null;
  events.forEach((e) => {
    const label = monthLabel(e.eventDate);
    if (!cur || cur.label !== label) { cur = { label, events: [] }; out.push(cur); }
    cur.events.push(e);
  });
  return out;
}

// mode: 'investor' (one household; "you" wording) | 'admin' (every household;
// "investor" wording, Who filter, household named on each row, who logged
// each row, CSV export of the filtered set, and — when onRowClick is given —
// a click on a row hands that row to the caller).
// aboveFilters (admin): ({ focus }) → node rendered between the header and
// the filter bar; focus(patch) resets the filters to `patch` and scrolls the
// ledger into view (the pulse uses it: household row → that household's rows
// in the window).
export function ActivityLedger({ events, members, mode = 'investor', header, emptyText, onRowClick, settlements = [], aboveFilters, canEditPool = false, onChanged }) {
  const [f, setF] = useState(EMPTY_FILTERS);
  const [openPool, setOpenPool] = useState(null); // pool entry row id whose drawer is open
  const [showFilters, setShowFilters] = useState(false);
  const admin = mode === 'admin';
  const you = admin ? 'Investor' : 'You';

  const byId = useMemo(() => Object.fromEntries((members || []).map((m) => [m.id, m])), [members]);

  const srcOptions = useMemo(() => accountOptions(events, 'srcKind', 'srcLabel'), [events]);
  const dstOptions = useMemo(() => accountOptions(events, 'dstKind', 'dstLabel'), [events]);
  const whoOpts = useMemo(() => (admin ? whoOptions(events, byId) : []), [admin, events, byId]);
  const hasPool = useMemo(() => events.some((e) => e.custody === 'pool'), [events]);
  const hasAdminLogged = useMemo(() => events.some((e) => e.enteredByRole === 'admin'), [events]);

  const filtered = useMemo(() => applyFilters(events, f, byId), [events, f, byId]);
  const groups = useMemo(() => groupByMonth(filtered), [filtered]);

  // Pool entries (gold / investment payouts, carryover) are pool credit, not
  // money moved through a loop — kept out of the "moved" total.
  const totals = useMemo(() => filtered.reduce((t, e) => ({
    amount: t.amount + (e.poolType ? 0 : Math.abs(Number(e.amount || 0))),
    value: t.value + Number(e.value || 0),
    kareem: t.kareem + Number(e.kareem || 0),
    investor: t.investor + Number(e.investor || 0),
  }), { amount: 0, value: 0, kareem: 0, investor: 0 }), [filtered]);

  const set = (k) => (ev) => setF((prev) => ({ ...prev, [k]: ev.target.value }));
  const clear = () => setF(EMPTY_FILTERS);
  const filtersRef = React.useRef(null);
  const focus = (patch) => {
    setF({ ...EMPTY_FILTERS, ...patch });
    const el = filtersRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // CSV of exactly what is on screen (the filtered set, newest first).
  const exportCsv = () => downloadCsv(toCsv(filtered, byId), csvFilename(f, byId));
  const rowProps = (e) => (onRowClick ? {
    role: 'button', tabIndex: 0,
    title: 'Open ' + ((byId[e.household] || byId[e.member] || {}).name || 'this household') + "'s activity",
    onClick: () => onRowClick(e),
    onKeyDown: (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onRowClick(e); } },
  } : {});
  const active = isActive(f);
  const nActive = activeCount(f);
  const nPanel = nActive - (f.q ? 1 : 0) - (admin && f.who ? 1 : 0);

  // Date presets (admin): this month / last 30 days / since the last
  // confirmed settlement (of the Who household when one is picked). Clicking
  // the active preset clears the date band again.
  const householdOf = (id) => byId[id]?.household || id;
  const lastSettle = useMemo(() => (admin ? lastSettlementDay(settlements, f.who, householdOf) : null), [admin, settlements, f.who, byId]);
  const preset = admin ? activePreset(f, new Date(), lastSettle) : null;
  const applyPreset = (name) => {
    const r = presetRange(name, new Date(), lastSettle);
    if (!r) return;
    setF((prev) => (preset === name ? { ...prev, from: '', to: '' } : { ...prev, from: r.from, to: r.to }));
  };
  const settleLabel = lastSettle ? new Date(lastSettle + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : null;

  return (
    <div className={'page activity-page' + (admin ? ' activity-page--admin' : '')}>
      {header}

      {typeof aboveFilters === 'function' ? aboveFilters({ focus }) : null}

      {events.length > 0 && (
        <div className="act-filters" ref={filtersRef}>
          <div className="act-filter-bar">
            {admin && (
              <select className="act-who" value={f.who} onChange={set('who')} aria-label="Investor">
                <option value="">All investors</option>
                {whoOpts.map((o) => (
                  <option key={o.id} value={o.id}>{o.indent ? '   ↳ ' : ''}{o.label}</option>
                ))}
              </select>
            )}
            <input
              className="act-search"
              type="search"
              value={f.q}
              onChange={set('q')}
              placeholder={admin ? 'Search investor, route, account, amount…' : 'Search route, account, amount…'}
              aria-label="Search activity"
            />
            <button
              type="button"
              className={'act-filter-toggle' + (showFilters || nPanel > 0 ? ' is-on' : '')}
              onClick={() => setShowFilters((v) => !v)}
              aria-expanded={showFilters}
            >
              Filters{nPanel > 0 ? <span className="act-filter-count">{nPanel}</span> : null}
            </button>
            {active && (
              <button type="button" className="act-filter-clear" onClick={clear}>Clear</button>
            )}
            {admin && (
              <button type="button" className="act-export" onClick={exportCsv} disabled={!filtered.length} title="Download the rows shown below as CSV">
                Export CSV{active ? ` (${filtered.length})` : ''}
              </button>
            )}
          </div>

          {showFilters && (
            <div className="act-filter-grid">
              <label className="act-field">
                <span className="act-field-lbl">From account</span>
                <select value={f.src} onChange={set('src')}>
                  <option value="">Any origin</option>
                  {srcOptions.map((o) => <option key={o.slug} value={o.slug}>{o.label}</option>)}
                </select>
              </label>
              <label className="act-field">
                <span className="act-field-lbl">To account</span>
                <select value={f.dst} onChange={set('dst')}>
                  <option value="">Any destination</option>
                  {dstOptions.map((o) => <option key={o.slug} value={o.slug}>{o.label}</option>)}
                </select>
              </label>
              <div className="act-field">
                <span className="act-field-lbl">Amount moved</span>
                <div className="act-field-pair">
                  <span className="act-pair-input">
                    <span className="act-pair-prefix">$</span>
                    <input type="number" inputMode="decimal" min="0" step="any" placeholder="min" value={f.minAmt} onChange={set('minAmt')} aria-label="Minimum amount" />
                  </span>
                  <span className="act-pair-sep">–</span>
                  <span className="act-pair-input">
                    <span className="act-pair-prefix">$</span>
                    <input type="number" inputMode="decimal" min="0" step="any" placeholder="max" value={f.maxAmt} onChange={set('maxAmt')} aria-label="Maximum amount" />
                  </span>
                </div>
              </div>
              <div className="act-field">
                <span className="act-field-lbl">Date</span>
                <div className="act-field-pair">
                  <input type="date" value={f.from} max={f.to || undefined} onChange={set('from')} aria-label="From date" />
                  <span className="act-pair-sep">–</span>
                  <input type="date" value={f.to} min={f.from || undefined} onChange={set('to')} aria-label="To date" />
                </div>
              </div>
              <label className="act-field">
                <span className="act-field-lbl">Value</span>
                <select value={f.value} onChange={set('value')}>
                  <option value="all">Any</option>
                  <option value="generated">Generated value</option>
                  <option value="none">No value on this leg</option>
                  <option value="pending">Awaiting Kareem</option>
                </select>
              </label>
              <label className="act-field">
                <span className="act-field-lbl">Who holds the value</span>
                <select value={f.holder} onChange={set('holder')}>
                  <option value="all">Anyone</option>
                  <option value="investor">{admin ? 'The investor holds it' : 'You hold it'}</option>
                  <option value="pool">Kareem holds it{hasPool ? '' : ' (none yet)'}</option>
                </select>
              </label>
              <label className="act-field">
                <span className="act-field-lbl">Logged by</span>
                <select value={f.loggedBy} onChange={set('loggedBy')}>
                  <option value="all">Anyone</option>
                  <option value="executor">{admin ? 'The investor' : 'You'}</option>
                  <option value="admin">{admin ? 'Admin on their behalf' : 'Admin on your behalf'}{hasAdminLogged ? '' : ' (none yet)'}</option>
                  <option value="agent">Agent / imported</option>
                </select>
              </label>
            </div>
          )}

          {admin && (
            <div className="act-presets" role="group" aria-label="Date presets">
              <button type="button" className={'act-preset' + (preset === 'thisMonth' ? ' is-on' : '')} onClick={() => applyPreset('thisMonth')}>This month</button>
              <button type="button" className={'act-preset' + (preset === 'last30' ? ' is-on' : '')} onClick={() => applyPreset('last30')}>Last 30 days</button>
              <button type="button" className={'act-preset' + (preset === 'sinceSettlement' ? ' is-on' : '')} onClick={() => applyPreset('sinceSettlement')} disabled={!lastSettle} title={lastSettle ? undefined : 'No confirmed settlement yet'}>
                Since last settlement{settleLabel ? ` (${settleLabel})` : ''}
              </button>
            </div>
          )}

          {(active || admin) && (
            <div className="act-filter-summary">
              <span>{active ? <><strong>{filtered.length}</strong> of {events.length} shown</> : <><strong>{events.length}</strong> row{events.length === 1 ? '' : 's'}</>}</span>
              <span className="act-sum-sep">·</span>
              <span>moved <strong className="mono">{fmt$(totals.amount)}</strong></span>
              <span className="act-sum-sep">·</span>
              <span>value <strong className="mono">{fmt$(totals.value)}</strong></span>
              <span className="act-sum-split">
                <span className="split-pill kareem">K {fmt$(totals.kareem)}</span>
                <span className="split-pill investor">{you} {fmt$(totals.investor)}</span>
              </span>
            </div>
          )}
        </div>
      )}

      {events.length === 0 ? (
        <div className="activity-empty">{emptyText || "No value events yet — they'll appear here once the matching engine logs the first movement."}</div>
      ) : filtered.length === 0 ? (
        <div className="activity-empty">
          Nothing matches these filters.
          <div><button type="button" className="act-filter-clear act-filter-clear--big" onClick={clear}>Clear filters</button></div>
        </div>
      ) : admin ? (
        <AdminLedgerBody rows={filtered} byId={byId} onRowClick={onRowClick} />
      ) : (
        groups.map((g) => (
          <section key={g.label}>
            <div className="act-month-head">{g.label}</div>
            <div className="activity-list">
              {g.events.map((e) => {
                const m = byId[e.member] || { name: e.member, initials: '?', color: '#94a3b8' };
                const h = admin && e.household && e.household !== e.member ? byId[e.household] : null;
                if (e.settleType) return <SettlementLedgerRow key={e.id} e={e} m={m} admin={admin} />;
                if (e.poolType) return (
                  <PoolLedgerRow key={e.id} e={e} m={m} canEdit={canEditPool}
                    isOpen={openPool === e.id} onToggle={() => setOpenPool(openPool === e.id ? null : e.id)} onChanged={onChanged} />
                );
                return (
                  <div key={e.id} className={'activity-row' + (onRowClick ? ' is-clickable' : '')} {...rowProps(e)}>
                    <div className="ar-date">{e.date}</div>
                    <div className="avatar" style={{ width: 26, height: 26, fontSize: 11, background: m.color }}>{m.initials}</div>
                    <div className="ar-flow">
                      <div className="ar-flow-line">
                        {e.flow}
                        {e.movementStatus === 'pending' ? <span className="ar-pending">Awaiting Kareem</span> : null}
                        {e.custody === 'pool' ? <span className="ar-pending ar-pool">Kareem holds</span> : null}
                      </div>
                      <div className="ar-flow-sub">
                        {m.name}{h ? <span className="ar-household"> · {h.name}'s household</span> : null} · {fmt$(e.amount)}
                        {e.noValue ? ' · no value on this leg' : <> @ <span className="mono">{fmtPct(e.rate)}</span></>}
                        {admin ? <span className="ar-logged"> · logged by {loggedByLabel(e, byId)}</span> : null}
                      </div>
                    </div>
                    <div className="ar-value">
                      <div className={'ar-value-amt' + (e.value === 0 ? ' is-zero' : '')}>{e.value === 0 ? '$0' : '+' + fmt$(e.value)}</div>
                      <div className="ar-value-split">
                        <span className="split-pill kareem">K {fmt$(e.kareem)}</span>
                        <span className="split-pill investor">{you} {fmt$(e.investor)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

// A cash settlement in the ledger (2026-10-23): a payment to Kareem, a payout
// from Kareem, or a waiver. Record only — no movement, no value, no custody.
// Edits live on the Settlements page (investor) and the admin queue.
function SettlementLedgerRow({ e, m, admin }) {
  const payout = e.settleType === 'payout';
  return (
    <div className={'activity-row activity-row--settle activity-row--settle-' + e.settleType} data-settlement={e.settlementId}>
      <div className="ar-date">{e.date}</div>
      <div className="avatar" style={{ width: 26, height: 26, fontSize: 11, background: m.color }}>{m.initials}</div>
      <div className="ar-flow">
        <div className="ar-flow-line">
          {e.flow}
          <span className={'ar-pending ar-settle' + (payout ? ' ar-settle-payout' : '')}>Settlement</span>
          {e.movementStatus === 'pending' ? <span className="ar-pending">Awaiting Kareem</span> : null}
        </div>
        <div className="ar-flow-sub">
          {settlementSub(e, !admin, m.name)}{e.memo ? <> · {e.memo}</> : null}
        </div>
      </div>
      <div className="ar-value">
        <div className="ar-value-amt" style={{ color: payout ? 'var(--green, #047857)' : e.settleSign < 0 ? 'var(--rose, #e11d48)' : undefined }}>{settlementAmount(e)}</div>
        <div className="ar-value-split"><span className="split-pill investor">{e.movementStatus === 'pending' ? 'Pending' : payout ? 'Paid to you' : e.settleType === 'payment' ? 'Paid to Kareem' : 'Waived'}</span></div>
      </div>
    </div>
  );
}

// A pool entry in the ledger: pool credit (or a carryover debit), never value.
// Admins can open it for Edit / Delete (PoolEntryActions keeps the paired gold
// payment / payout row in step).
function PoolLedgerRow({ e, m, canEdit, isOpen, onToggle, onChanged }) {
  const neg = Number(e.poolAmount) < 0;
  const clickable = canEdit ? {
    role: 'button', tabIndex: 0, 'aria-expanded': isOpen, onClick: onToggle,
    onKeyDown: (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onToggle(); } },
  } : {};
  return (
    <>
      <div className={'activity-row activity-row--pool' + (canEdit ? ' is-clickable' : '') + (isOpen ? ' is-open' : '')} {...clickable}>
        <div className="ar-date">{e.date}</div>
        <div className="avatar" style={{ width: 26, height: 26, fontSize: 11, background: m.color }}>{m.initials}</div>
        <div className="ar-flow">
          <div className="ar-flow-line">
            {e.flow}
            <span className="ar-pending ar-pool">Pool</span>
            {e.movementStatus === 'pending' ? <span className="ar-pending">Awaiting Kareem</span> : null}
          </div>
          <div className="ar-flow-sub">{m.name} · {neg ? 'owed back to Kareem from' : 'credited to'} the TK-C pool · no value on this leg</div>
        </div>
        <div className="ar-value">
          <div className="ar-value-amt" style={{ color: neg ? 'var(--rose, #e11d48)' : undefined }}>{neg ? '−' : '+'}{fmt$(e.amount, 2)}</div>
          <div className="ar-value-split"><span className="split-pill investor">Pool</span></div>
        </div>
      </div>
      {isOpen && canEdit && (
        <div className="activity-drawer">
          {e.note ? (
            <div className="ad-info">
              <span className="ad-info-lbl">Note</span>
              <span className="ad-info-val">{e.note}</span>
            </div>
          ) : null}
          <div className="ad-actions"><PoolEntryActions entry={e} onDone={onChanged} /></div>
        </div>
      )}
    </>
  );
}

export default function Activity({ member, profile }) {
  const { status, data, reload } = useInvestmentData(member, profile);
  const events = (data && data.allEvents) || [];
  const members = (data && data.members) || [];

  if (status === 'loading') {
    return <div className="page"><div className="activity-empty">Loading activity…</div></div>;
  }

  const header = (
    <div className="page-title-row">
      <div>
        <div className="page-eyebrow">Ledger</div>
        <h1 className="page-title">Activity</h1>
        <p className="page-sub">{events.length} value event{events.length === 1 ? '' : 's'} · every movement Kareem's engine logged.</p>
      </div>
    </div>
  );

  return <ActivityLedger events={events} members={members} mode="investor" header={header}
    canEditPool={profile?.role === 'admin'} onChanged={reload} />;
}
